import React, {
  createContext,
  useContext,
  useReducer,
  useRef,
  useCallback,
  ReactNode,
  Dispatch,
  RefObject,
} from "react";

type State = {
  query: string;
};

type Action = {
  type: "SET_QUERY";
  payload: string;
};

const ProductSearchContext = createContext<{
  state: State;
  dispatch: Dispatch<Action>;
  clearInputRef: RefObject<(() => void) | null>;
}>({
  state: { query: "" },
  dispatch: () => null,
  clearInputRef: { current: null },
});

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "SET_QUERY":
      return { ...state, query: action.payload };
    default:
      return state;
  }
};

type ProductSearchContextProviderProps = {
  children: ReactNode;
  query: string;
};

export const ProductSearchContextProvider = ({
  children,
  query,
}: ProductSearchContextProviderProps) => {
  const initialState: State = { query };
  const [state, dispatch] = useReducer(reducer, initialState);
  const clearInputRef = useRef<(() => void) | null>(null);

  return (
    <ProductSearchContext.Provider value={{ state, dispatch, clearInputRef }}>
      {children}
    </ProductSearchContext.Provider>
  );
};

export const useProductSearchContext = () => useContext(ProductSearchContext);

export const useProductSearchQuery = () => {
  const { dispatch, state, clearInputRef } = useProductSearchContext();

  const setQuery = (query: string) => {
    dispatch({ type: "SET_QUERY", payload: query });
  };

  const registerClearInput = useCallback(
    (fn: (() => void) | null) => {
      clearInputRef.current = fn;
    },
    [clearInputRef],
  );

  const resetSearch = () => {
    dispatch({ type: "SET_QUERY", payload: "" });
    clearInputRef.current?.();
  };

  return {
    setQuery,
    resetSearch,
    registerClearInput,
    query: state.query,
  };
};
